// React
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from "react";
// Navigation
import {
  useNavigate,
  useParams,
} from "react-router-dom";
// FHIR
import {
  Encounter,
  Reference,
} from "fhir/r4";
// Translation
import i18n from "i18next";
// HL7 front library
import {
  StatusTag,
  Title,
} from "@fyrstain/hl7-front-library";
// Component
import AcesoPage from "src/components/AcesoPage/AcesoPage";
// Services
import {
  formatDate,
  formatPeriod,
  getCodeableConceptValue,
  getCodeableConceptValues,
  getIdentifierValues,
  getNarrativeText,
  getReferenceIdentifier,
  getReferencePath,
  getReferenceValue,
  getStatusFlavor,
} from "src/services/resourceDetails.utils";
// Style
import "./EncounterDetailsPage.css";

const EncounterDetailsPage: FunctionComponent = () => {
  const { resourceId } = useParams();
  const navigate = useNavigate();

  const [encounter, setEncounter] =
    useState<Encounter>();
  const [loading, setLoading] = useState(true);

  //////////////////////////////
  //           Error          //
  //////////////////////////////

  const onError = useCallback((error?: unknown) => {
    console.error(
      "Encounter details error:",
      error ?? "Network or FHIR error",
    );
  }, []);

  //////////////////////////////
  //          Request         //
  //////////////////////////////

  useEffect(() => {
    if (!resourceId) {
      setLoading(false);
      return;
    }

    const controller = new AbortController();

    fetch(
      `${process.env.REACT_APP_FHIR_URL}/Encounter/${resourceId}`,
      {
        signal: controller.signal,
      },
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `FHIR request failed with status ${response.status}`,
          );
        }

        return response.json() as Promise<Encounter>;
      })
      .then((data) => {
        setEncounter(data);
      })
      .catch((error: unknown) => {
        if (
          error instanceof DOMException &&
          error.name === "AbortError"
        ) {
          return;
        }

        onError(error);
      })
      .finally(() => {
        setLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, [onError, resourceId]);

  //////////////////////////////
  //         Functions        //
  //////////////////////////////

  function renderStringList(values: string[]) {
    return (
      <ul className="resource-details-list">
        {values.map((value, index) => (
          <li key={`${value}-${index}`}>
            {value}
          </li>
        ))}
      </ul>
    );
  }

  function renderReferences(
    references?: Reference[],
  ) {
    if (!references?.length) {
      return (
        <span className="resource-details-value">
          {i18n.t("general.notavailable")}
        </span>
      );
    }

    return renderStringList(
      references.map((reference) =>
        getReferenceValue(reference),
      ),
    );
  }

  //////////////////////////////
  //          Content         //
  //////////////////////////////

  return (
    <AcesoPage
      loading={loading}
      titleKey="pathway.tabs.visits"
      needsLogin={true}
    >
      {!encounter ? (
        <div className="alert alert-info">
          {i18n.t("general.notavailable")}
        </div>
      ) : (
        <div className="resource-details-page">
          <div className="resource-details-toolbar">
            <button
              className="resource-details-back-button"
              onClick={() => navigate(-1)}
              type="button"
            >
              {i18n.t("button.backtolist")}
            </button>
          </div>

          <section className="resource-details-section">
            <Title
              level={2}
              content={i18n.t(
                "details.sections.patient",
              )}
            />

            <div className="row g-3">
              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t("label.patient")}
                  </span>

                  <span className="resource-details-value">
                    {getReferenceValue(
                      encounter.subject,
                    )}
                  </span>
                </div>
              </div>

              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t(
                      "label.patientidentifier",
                    )}
                  </span>

                  <span className="resource-details-value">
                    {getReferenceIdentifier(
                      encounter.subject,
                    )}
                  </span>
                </div>
              </div>

              <div className="col-12">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t(
                      "label.patientreference",
                    )}
                  </span>

                  <span className="resource-details-value">
                    {getReferencePath(
                      encounter.subject,
                    )}
                  </span>
                </div>
              </div>
            </div>
          </section>

          <section className="resource-details-section">
            <Title
              level={2}
              content={i18n.t(
                "details.sections.encounter",
              )}
            />

            <div className="row g-3">
              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t("label.status")}
                  </span>

                  <StatusTag
                    message={i18n.t(
                      `encounter.status.${encounter.status}`,
                      {
                        defaultValue: encounter.status,
                      },
                    )}
                    flavor={getStatusFlavor(
                      encounter.status,
                    )}
                  />
                </div>
              </div>

              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t("label.class")}
                  </span>

                  <span className="resource-details-value">
                    {encounter.class.display ??
                      encounter.class.code ??
                      encounter.class.system}
                  </span>
                </div>
              </div>

              <div className="col-12">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t(
                      "label.visitidentifiers",
                    )}
                  </span>

                  {renderStringList(
                    getIdentifierValues(
                      encounter.identifier,
                    ),
                  )}
                </div>
              </div>

              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t("label.priority")}
                  </span>

                  <span className="resource-details-value">
                    {getCodeableConceptValue(
                      encounter.priority,
                    )}
                  </span>
                </div>
              </div>

              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t("label.servicetype")}
                  </span>

                  <span className="resource-details-value">
                    {getCodeableConceptValue(
                      encounter.serviceType,
                    )}
                  </span>
                </div>
              </div>

              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t("label.startdate")}
                  </span>

                  <span className="resource-details-value">
                    {formatDate(
                      encounter.period?.start,
                    )}
                  </span>
                </div>
              </div>

              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t("label.enddate")}
                  </span>

                  <span className="resource-details-value">
                    {formatDate(
                      encounter.period?.end,
                    )}
                  </span>
                </div>
              </div>

              <div className="col-12">
                <div className="resource-details-card resource-details-static-card">
                  <span className="resource-details-label">
                    {i18n.t("label.type")}
                  </span>

                  {renderStringList(
                    getCodeableConceptValues(
                      encounter.type,
                    ),
                  )}
                </div>
              </div>

              <div className="col-12">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t("label.text")}
                  </span>

                  <span className="resource-details-value">
                    {getNarrativeText(
                      encounter.text?.div,
                    )}
                  </span>
                </div>
              </div>
            </div>
          </section>

          <section className="resource-details-section">
            <Title
              level={2}
              content={i18n.t(
                "details.sections.reasons",
              )}
            />

            <div className="row g-3">
              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t("label.reasoncodes")}
                  </span>

                  {renderStringList(
                    getCodeableConceptValues(
                      encounter.reasonCode,
                    ),
                  )}
                </div>
              </div>

              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t(
                      "label.reasonreferences",
                    )}
                  </span>

                  {renderReferences(
                    encounter.reasonReference,
                  )}
                </div>
              </div>
            </div>
          </section>

          <section className="resource-details-section">
            <Title
              level={2}
              content={i18n.t(
                "details.sections.diagnosis",
              )}
            />

            <div className="row g-3">
              {encounter.diagnosis?.length ? (
                encounter.diagnosis.map(
                  (diagnosis, index) => (
                    <div
                      className="col-12"
                      key={index}
                    >
                      <div className="resource-details-card">
                        <span className="resource-details-label">
                          {i18n.t("label.diagnosis")}{" "}
                          {index + 1}
                        </span>

                        <div className="resource-details-value">
                          <div>
                            <strong>
                              {i18n.t(
                                "label.condition",
                              )}
                              :
                            </strong>{" "}
                            {getReferenceValue(
                              diagnosis.condition,
                            )}
                          </div>

                          <div>
                            <strong>
                              {i18n.t("label.role")}:
                            </strong>{" "}
                            {getCodeableConceptValue(
                              diagnosis.use,
                            )}
                          </div>

                          <div>
                            <strong>
                              {i18n.t("label.rank")}:
                            </strong>{" "}
                            {diagnosis.rank ??
                              i18n.t(
                                "general.notavailable",
                              )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ),
                )
              ) : (
                <div className="col-12">
                  <div className="resource-details-card">
                    <span className="resource-details-value">
                      {i18n.t("general.notavailable")}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </section>

          <section className="resource-details-section">
            <Title
              level={2}
              content={i18n.t(
                "details.sections.participants",
              )}
            />

            <div className="row g-3">
              {encounter.participant?.length ? (
                encounter.participant.map(
                  (participant, index) => (
                    <div
                      className="col-12"
                      key={index}
                    >
                      <div className="resource-details-card">
                        <span className="resource-details-label">
                          {i18n.t(
                            "label.participant",
                          )}{" "}
                          {index + 1}
                        </span>

                        <div className="resource-details-value">
                          <div>
                            <strong>
                              {i18n.t("label.type")}:
                            </strong>{" "}
                            {getCodeableConceptValues(
                              participant.type,
                            ).join(", ")}
                          </div>

                          <div>
                            <strong>
                              {i18n.t(
                                "label.individual",
                              )}
                              :
                            </strong>{" "}
                            {getReferenceValue(
                              participant.individual,
                            )}
                          </div>

                          <div>
                            <strong>
                              {i18n.t("label.period")}:
                            </strong>{" "}
                            {formatPeriod(
                              participant.period?.start,
                              participant.period?.end,
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ),
                )
              ) : (
                <div className="col-12">
                  <div className="resource-details-card">
                    <span className="resource-details-value">
                      {i18n.t("general.notavailable")}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </section>

          <section className="resource-details-section">
            <Title
              level={2}
              content={i18n.t(
                "details.sections.hospitalization",
              )}
            />

            <div className="row g-3">
              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t("label.admitsource")}
                  </span>

                  <span className="resource-details-value">
                    {getCodeableConceptValue(
                      encounter.hospitalization
                        ?.admitSource,
                    )}
                  </span>
                </div>
              </div>

              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t("label.readmission")}
                  </span>

                  <span className="resource-details-value">
                    {getCodeableConceptValue(
                      encounter.hospitalization
                        ?.reAdmission,
                    )}
                  </span>
                </div>
              </div>

              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t("label.origin")}
                  </span>

                  <span className="resource-details-value">
                    {getReferenceValue(
                      encounter.hospitalization
                        ?.origin,
                    )}
                  </span>
                </div>
              </div>

              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t("label.destination")}
                  </span>

                  <span className="resource-details-value">
                    {getReferenceValue(
                      encounter.hospitalization
                        ?.destination,
                    )}
                  </span>
                </div>
              </div>

              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t(
                      "label.specialcourtesy",
                    )}
                  </span>

                  {renderStringList(
                    getCodeableConceptValues(
                      encounter.hospitalization
                        ?.specialCourtesy,
                    ),
                  )}
                </div>
              </div>

              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t(
                      "label.specialarrangements",
                    )}
                  </span>

                  {renderStringList(
                    getCodeableConceptValues(
                      encounter.hospitalization
                        ?.specialArrangement,
                    ),
                  )}
                </div>
              </div>
            </div>
          </section>

          <section className="resource-details-section">
            <Title
              level={2}
              content={i18n.t(
                "details.sections.locations",
              )}
            />

            <div className="row g-3">
              {encounter.location?.length ? (
                encounter.location.map(
                  (location, index) => (
                    <div
                      className="col-12"
                      key={index}
                    >
                      <div className="resource-details-card">
                        <span className="resource-details-label">
                          {i18n.t("label.location")}{" "}
                          {index + 1}
                        </span>

                        <div className="resource-details-value">
                          <div>
                            <strong>
                              {i18n.t(
                                "label.location",
                              )}
                              :
                            </strong>{" "}
                            {getReferenceValue(
                              location.location,
                            )}
                          </div>

                          <div>
                            <strong>
                              {i18n.t("label.status")}:
                            </strong>{" "}
                            {location.status ??
                              i18n.t(
                                "general.notavailable",
                              )}
                          </div>

                          <div>
                            <strong>
                              {i18n.t(
                                "label.physicaltype",
                              )}
                              :
                            </strong>{" "}
                            {getCodeableConceptValue(
                              location.physicalType,
                            )}
                          </div>

                          <div>
                            <strong>
                              {i18n.t("label.period")}:
                            </strong>{" "}
                            {formatPeriod(
                              location.period?.start,
                              location.period?.end,
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ),
                )
              ) : (
                <div className="col-12">
                  <div className="resource-details-card">
                    <span className="resource-details-value">
                      {i18n.t("general.notavailable")}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </section>

          <section className="resource-details-section">
            <Title
              level={2}
              content={i18n.t(
                "details.sections.references",
              )}
            />

            <div className="row g-3">
              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t(
                      "label.serviceprovider",
                    )}
                  </span>

                  <span className="resource-details-value">
                    {getReferenceValue(
                      encounter.serviceProvider,
                    )}
                  </span>
                </div>
              </div>

              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t(
                      "label.episodeofcare",
                    )}
                  </span>

                  {renderReferences(
                    encounter.episodeOfCare,
                  )}
                </div>
              </div>

              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t(
                      "label.appointments",
                    )}
                  </span>

                  {renderReferences(
                    encounter.appointment,
                  )}
                </div>
              </div>

              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t("label.basedon")}
                  </span>

                  {renderReferences(encounter.basedOn)}
                </div>
              </div>

              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t("label.accounts")}
                  </span>

                  {renderReferences(encounter.account)}
                </div>
              </div>

              <div className="col-md-6">
                <div className="resource-details-card">
                  <span className="resource-details-label">
                    {i18n.t("label.partof")}
                  </span>

                  <span className="resource-details-value">
                    {getReferenceValue(
                      encounter.partOf,
                    )}
                  </span>
                </div>
              </div>
            </div>
          </section>
        </div>
      )}
    </AcesoPage>
  );
};

export default EncounterDetailsPage;
